<script lang="ts">
  /**
   * Lyktan login-vy (S-04-02, omarbetad i S-04-16).
   *
   * Två steg:
   *  1. E-post + lösenord. Vid OK växlar vyn till MFA-steget.
   *  2. TOTP-kod (6 siffror). Vid OK kallas onAuthenticated så App.svelte
   *     kan rendera huvud-vyn.
   *
   * Hard-rules:
   *  - Inga tokens lagras klient-side. Backend sätter HttpOnly-cookie.
   *  - State för stegväxling är bara `$state`-runes — försvinner vid reload.
   *  - Felmeddelanden på svenska, generiska för credential-fel
   *    (user-enumeration-skydd).
   *
   * Heading-hierarki: App-shell äger sidans H1 ("Lyktan — Härden admin").
   * Denna vy börjar med H2 så strukturen är App H1 → vy H2 → sub-rubrik H3,
   * konsekvent med HearthsView, AdminUsersView och MfaSetupView.
   */
  import QRCode from 'qrcode';
  import { frejaInitiate, frejaPoll, login, verifyMfa } from './authClient';

  type Stage = 'credentials' | 'mfa' | 'freja';

  type AuthenticatedInfo = { userId: string; role: string };
  type Props = {
    // eslint-disable-next-line no-unused-vars
    onAuthenticated: (info: AuthenticatedInfo) => void;
  };

  const { onAuthenticated }: Props = $props();

  let stage = $state<Stage>('credentials');
  let email = $state('');
  let password = $state('');
  let totpCode = $state('');
  let errorMessage: string | null = $state(null);
  let isSubmitting = $state(false);

  // Fokus-management vid stage-byte: skärmläsare och tangentbordsanvändare
  // ska landa på rätt input direkt utan att behöva tabba ned till formuläret.
  let emailInput: HTMLInputElement | undefined = $state();
  let totpInput: HTMLInputElement | undefined = $state();
  // Freja-steget har inga fält att fokusera — utan en explicit
  // fokus-mottagare faller fokus till <body> och tangentbords- och
  // skärmläsaranvändare tappar sin plats i flödet.
  let frejaStageEl: HTMLDivElement | undefined = $state();

  $effect(() => {
    // Trigga endast på stage-byten — vi läser bara `stage` inom effekten.
    if (stage === 'mfa') {
      queueMicrotask(() => totpInput?.focus());
    } else if (stage === 'freja') {
      queueMicrotask(() => frejaStageEl?.focus());
    } else {
      queueMicrotask(() => emailInput?.focus());
    }
  });

  async function handleCredentialsSubmit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (isSubmitting) return;
    errorMessage = null;
    isSubmitting = true;
    try {
      const result = await login(email, password);
      if (result.kind === 'mfa_required') {
        // Lösenordet får inte ligga kvar i runes-state efter steget —
        // hindrar att en accidentell re-render eller komponent-läcka
        // visar det igen.
        password = '';
        stage = 'mfa';
        return;
      }
      errorMessage = result.message;
    } finally {
      isSubmitting = false;
    }
  }

  async function handleMfaSubmit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (isSubmitting) return;
    errorMessage = null;
    isSubmitting = true;
    try {
      const trimmed = totpCode.trim();
      if (!/^\d{6}$/.test(trimmed)) {
        errorMessage = 'Koden måste vara sex siffror.';
        return;
      }
      const result = await verifyMfa(trimmed);
      if (result.kind === 'authenticated') {
        totpCode = '';
        onAuthenticated({ userId: result.userId, role: result.role });
        return;
      }
      // Backend raderar challenge-cookien vid ALLA misslyckade utfall —
      // även vid fel TOTP-kod (S-04-02-followup, brute-force-skydd). Ett
      // nytt försök i MFA-steget kan därför aldrig lyckas; vi går alltid
      // tillbaka till steg 1 och säger det rakt ut istället för att
      // inbjuda till ett försök som är dömt att falla på "har gått ut".
      //
      // Grenar på felKODEN, inte på texten — en copy-ändring får inte
      // tyst återinföra förvirringen.
      totpCode = '';
      stage = 'credentials';
      errorMessage =
        result.code === 'invalid_mfa_code'
          ? 'Felaktig MFA-kod. Av säkerhetsskäl måste du logga in från början igen.'
          : result.message;
    } finally {
      isSubmitting = false;
    }
  }

  function backToCredentials(): void {
    stage = 'credentials';
    totpCode = '';
    errorMessage = null;
    frejaGeneration += 1;
    frejaQrUrl = '';
    frejaDelivered = false;
  }

  // ---- Freja eID-flöde (S-04-37) ----
  //
  // initiate → rendera QR klient-side (CSP-säkert, ingen extern bild-
  // hämtning) → polla var 3:e sekund tills backend satt session-cookien.
  // Transaktionsfönstret är ~2 min (Freja EXPIRED vid 120–135 s) —
  // vid utgång visas "Börja om"-knappen som startar en ny transaktion.
  // `frejaGeneration` invaliderar en pågående poll-loop vid avbryt/
  // omstart så gamla loopar aldrig skriver state efter stage-byte.
  let frejaQrUrl: string = $state('');
  let frejaDelivered: boolean = $state(false);
  let frejaDeepLink: string = $state('');
  let frejaGeneration = 0;

  // Texten i den persistenta live-regionen. Tom sträng när inget är på
  // gång — regionen finns kvar i DOM:en, bara innehållet byts.
  const frejaStatusText = $derived(
    stage !== 'freja'
      ? ''
      : frejaQrUrl
        ? frejaDelivered
          ? 'Öppnad i mobilen — godkänn i Freja-appen.'
          : 'Väntar på att du skannar QR-koden.'
        : errorMessage
          ? ''
          : 'Hämtar QR-kod…',
  );

  async function startFreja(): Promise<void> {
    stage = 'freja';
    errorMessage = null;
    frejaQrUrl = '';
    frejaDeepLink = '';
    frejaDelivered = false;
    const generation = ++frejaGeneration;

    const init = await frejaInitiate();
    if (generation !== frejaGeneration || stage !== 'freja') return;
    if (init.kind === 'failure') {
      errorMessage = init.message;
      return;
    }

    try {
      frejaQrUrl = await QRCode.toDataURL(init.qrData, {
        errorCorrectionLevel: 'M',
        margin: 2,
        width: 480,
        // Klassisk mörk-på-ljus QR — Freja-appens skanner är verifierad
        // mot detta format (E2E 2026-08-17). Invertera inte.
        color: { dark: '#0a0a0a', light: '#f2ece0' },
      });
      frejaDeepLink = init.qrData;
    } catch {
      errorMessage = 'Kunde inte skapa QR-kod. Försök igen.';
      return;
    }

    const startedAt = Date.now();
    while (generation === frejaGeneration && stage === 'freja' && Date.now() - startedAt < 150_000) {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      if (generation !== frejaGeneration || stage !== 'freja') return;

      const result = await frejaPoll(init.authRef);
      if (generation !== frejaGeneration || stage !== 'freja') return;

      if (result.kind === 'authenticated') {
        frejaQrUrl = '';
        onAuthenticated({ userId: result.userId, role: result.role });
        return;
      }
      if (result.kind === 'failure') {
        errorMessage = result.message;
        frejaQrUrl = '';
        return;
      }
      frejaDelivered = result.status === 'DELIVERED_TO_MOBILE';
    }

    if (generation === frejaGeneration && stage === 'freja') {
      errorMessage = 'QR-koden har gått ut. Börja om för att få en ny.';
      frejaQrUrl = '';
    }
  }
</script>

<section class="login" aria-labelledby="login-rubrik">
  <p class="eyebrow">Inloggning</p>
  <h2 id="login-rubrik">
    {#if stage === 'credentials'}
      Logga in
    {:else if stage === 'freja'}
      Freja eID
    {:else}
      Tvåfaktor
    {/if}
  </h2>
  <p class="lead">
    {#if stage === 'credentials'}
      Ange din admin-e-post och lösenord. Du behöver också din MFA-kod i nästa steg.
    {:else if stage === 'freja'}
      {#if frejaQrUrl}
        Skanna QR-koden med Freja eID-appen och godkänn inloggningen. Koden gäller i cirka två
        minuter.
      {:else}
        Startar inloggning med Freja eID.
      {/if}
    {:else}
      Ange den 6-siffriga koden från din authenticator-app.
    {/if}
  </p>

  {#if errorMessage}
    <div class="alert error" role="alert">{errorMessage}</div>
  {/if}

  <!-- Persistent live-region. Måste ligga UTANFÖR alla {#if}-block:
       en region som rivs och återskapas med sitt innehåll annonseras
       opålitligt (eller inte alls) av NVDA/JAWS/VoiceOver. Här byts
       bara textinnehållet. -->
  <p class="sr-status" role="status" aria-live="polite">{frejaStatusText}</p>

  {#if stage === 'credentials'}
    <form onsubmit={handleCredentialsSubmit} novalidate>
      <label>
        <span>E-post</span>
        <input
          type="email"
          name="email"
          autocomplete="username"
          required
          bind:value={email}
          bind:this={emailInput}
          disabled={isSubmitting}
        />
      </label>
      <label>
        <span>Lösenord</span>
        <input
          type="password"
          name="password"
          autocomplete="current-password"
          required
          bind:value={password}
          disabled={isSubmitting}
        />
      </label>
      <button type="submit" class="btn primary" disabled={isSubmitting || !email || !password}>
        {isSubmitting ? 'Loggar in…' : 'Logga in'}
      </button>
    </form>
    <!-- Freja-knappen ligger UTANFÖR formuläret: den hör inte till
         credentials-inskickningen. "eller" läses upp, vilket är önskvärt. -->
    <div class="divider"><span>eller</span></div>
    <button type="button" class="btn secondary" onclick={startFreja} disabled={isSubmitting}>
      Logga in med Freja eID
    </button>
  {:else if stage === 'freja'}
    <!-- tabindex=-1: steget har inga formulärfält, så containern är
         fokus-mottagare vid stage-bytet. -->
    <div class="freja-stage" bind:this={frejaStageEl} tabindex="-1">
      {#if frejaQrUrl}
        <div class="qr-frame">
          <img src={frejaQrUrl} alt="QR-kod för Freja eID-inloggning" />
        </div>
        <!-- Same-device-väg: på mobil/desktop med Freja installerat
             öppnar länken appen direkt. Ger också en icke-visuell väg
             genom steget — en QR är per definition oåtkomlig för
             skärmläsare. -->
        <a class="freja-devicelink" href={frejaDeepLink}>Öppna Freja eID på den här enheten</a>
      {:else if !errorMessage}
        <p class="freja-hint">Hämtar QR-kod…</p>
      {/if}
      <div class="actions">
        <button type="button" class="btn secondary" onclick={backToCredentials}> Avbryt </button>
        {#if errorMessage}
          <button type="button" class="btn primary" onclick={startFreja}> Börja om </button>
        {/if}
      </div>
    </div>
  {:else}
    <form onsubmit={handleMfaSubmit} novalidate>
      <label>
        <span>MFA-kod</span>
        <input
          type="text"
          name="totp"
          inputmode="numeric"
          pattern="[0-9]{'{6}'}"
          maxlength="6"
          autocomplete="one-time-code"
          required
          bind:value={totpCode}
          bind:this={totpInput}
          disabled={isSubmitting}
        />
      </label>
      <div class="actions">
        <button type="button" class="btn secondary" onclick={backToCredentials}> Tillbaka </button>
        <button type="submit" class="btn primary" disabled={isSubmitting || totpCode.length !== 6}>
          {isSubmitting ? 'Verifierar…' : 'Verifiera'}
        </button>
      </div>
    </form>
  {/if}
</section>

<style>
  .login {
    max-width: 480px;
    margin: 2rem auto;
    padding: 1.75rem;
    background: var(--bg-elev);
    border: 1px solid var(--rule);
    border-radius: 4px;
  }
  .eyebrow {
    font-size: 0.85rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--accent);
    font-weight: 600;
    margin: 0 0 0.6rem;
  }
  h2 {
    font-size: 1.9rem;
    line-height: 1.1;
    font-weight: 700;
    letter-spacing: -0.025em;
    margin: 0 0 0.6rem;
  }
  .lead {
    margin: 0 0 1.5rem;
    color: var(--text-dim);
  }
  .alert {
    padding: 0.75rem 1rem;
    border-radius: 4px;
    margin-bottom: 1.25rem;
    font-size: 0.92rem;
    border: 1px solid var(--rule);
  }
  .alert.error {
    color: var(--danger);
    background: var(--danger-soft);
    border-color: var(--danger);
  }
  form {
    display: grid;
    gap: 1rem;
  }
  label {
    display: grid;
    gap: 0.4rem;
    font-size: 0.85rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--text-dim);
    font-weight: 600;
  }
  input {
    background: var(--bg);
    color: var(--text);
    border: 1px solid var(--rule);
    border-radius: 4px;
    padding: 0.6rem 0.8rem;
    font: inherit;
    font-size: 0.95rem;
    text-transform: none;
    letter-spacing: normal;
    font-weight: 400;
    color-scheme: dark;
  }
  input:focus {
    outline: 2px solid var(--accent);
    outline-offset: 0;
    border-color: var(--accent);
  }
  .btn {
    appearance: none;
    border: 1px solid transparent;
    border-radius: 4px;
    padding: 0.6rem 1.1rem;
    font: inherit;
    font-size: 0.9rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition:
      background-color 120ms ease,
      border-color 120ms ease,
      color 120ms ease;
  }
  .btn.primary {
    background: var(--accent);
    color: var(--bg);
    border-color: var(--accent);
  }
  .btn.primary:hover:not(:disabled),
  .btn.primary:focus-visible:not(:disabled) {
    background: var(--glow);
    border-color: var(--glow);
  }
  .btn.secondary {
    background: transparent;
    color: var(--accent);
    border-color: var(--accent);
  }
  .btn.secondary:hover:not(:disabled),
  .btn.secondary:focus-visible:not(:disabled) {
    background: var(--accent-soft);
    color: var(--glow);
  }
  .btn:focus-visible {
    outline: 2px solid var(--glow);
    outline-offset: 2px;
  }
  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .actions {
    display: flex;
    gap: 0.75rem;
    justify-content: flex-end;
  }
  .divider {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: var(--text-dim);
    font-size: 0.8rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }
  .divider::before,
  .divider::after {
    content: '';
    flex: 1;
    border-top: 1px solid var(--rule);
  }
  .freja-stage {
    display: grid;
    gap: 1rem;
    justify-items: center;
  }
  .freja-stage:focus {
    outline: none;
  }
  .freja-stage:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 4px;
  }
  .qr-frame {
    background: var(--qr-paper);
    padding: 0.75rem;
    border-radius: 4px;
    line-height: 0;
  }
  .qr-frame img {
    width: 220px;
    height: 220px;
    image-rendering: pixelated;
  }
  .freja-hint {
    margin: 0;
    color: var(--text-dim);
    font-size: 0.92rem;
  }
  .freja-devicelink {
    color: var(--accent);
    font-size: 0.9rem;
  }
  .freja-devicelink:focus-visible {
    outline: 2px solid var(--glow);
    outline-offset: 2px;
  }
</style>
